/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { catchError, filter, finalize, map, take, timeout } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { CallEvent } from '../enums/call-event';
import { AuthorizationType, Trigger, TriggerTemplate, TriggerType } from '../models/client-settings';
import { ConversationInfo, IncomingCallInfo } from '../models/incoming-call-info';
import { OpenInBrowserMessage } from '../models/web-view-messages/open-in-browser-message';
import { TriggerResponseInfo } from '../models/trigger-response-info';
import { CommandMessage } from '../models/web-view-messages/command-message';
import { AuthRequest } from '../auth-config';
import { MessageService } from './message.service';
import { CommandBusService } from './command-bus.service';
import { SettingsService } from './settings.service';
import { PlaceholderService } from './placeholder.service';
import { LoggingService } from './logging.service';
import { ServicesService } from './services.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class TriggerService {
  private callTriggersInfo: TriggerResponseInfo = {};

  public constructor(
    private readonly settingsService: SettingsService,
    private readonly placeholderService: PlaceholderService,
    private readonly servicesService: ServicesService,
    private readonly authService: AuthService,
    private readonly commandBus: CommandBusService,
    private readonly loggingService: LoggingService,
  ) {}

  public processCallEvent(event: CallEvent, callInfo: ConversationInfo | null = null): void {
    if (!callInfo) {
      this.loggingService.Warning(`TriggerService: processCallEvent(${event}) skipped — no callInfo`);
      return;
    }

    this.loggingService.Info(`TriggerService: processCallEvent(${event}) for ${callInfo.id}`);

    this.getTriggersForEvent(event, callInfo)
      .pipe(
        take(1),
        finalize(() => {
          this.callTriggersInfo = {};
        }),
      )
      .subscribe((triggers) => {
        if (triggers && triggers.length) {
          this.loggingService.Info(`TriggerService: firing ${triggers.length} trigger(s) for event ${event}`);
          this.fireTriggers(triggers, callInfo);
        }
      });
  }

  private getTriggersForEvent(event: CallEvent, callInfo: ConversationInfo): Observable<Trigger[]> {
    const callTemplates$ = callInfo.isServiceCall
      ? this.servicesService.getServiceCallTemplates(callInfo.teamInfo?.teamId)
      : this.getDirectCallTemplates();

    return callTemplates$.pipe(
      map((callTemplates) =>
        callTemplates?.filter(
          (t) =>
            (event === CallEvent.IncomingCall && t.triggerType === TriggerType.Ringing) ||
            (event === CallEvent.CallAccepted && t.triggerType === TriggerType.Accepted) ||
            (event === CallEvent.TaskTerminated && t.triggerType === TriggerType.TaskTerminated),
        ),
      ),
      map((callTemplates) =>
        callTemplates?.filter((t) => {
          if (callInfo.isOutboundServiceCall) {
            return t.callOrigin.outbound;
          }

          if (callInfo.isExternalTaskSession) {
            return t.callOrigin.externalTask;
          }

          if (callInfo.isImSession) {
            return t.callOrigin.instantMessaging;
          }

          if (callInfo.isPstn) {
            return t.callOrigin.pstn;
          }

          if (callInfo.modality === Modality.Email) {
            return t.callOrigin.email;
          }

          // Checking on internal and external types must be the last step
          if (callInfo.isInternal) {
            return t.callOrigin.internal;
          }

          return t.callOrigin.external;
        }),
      ),
      map((callTemplates) => {
        const triggers: Trigger[] = [].concat(
          ...(callTemplates?.map((template) => template.triggers.sort((a, b) => a.position - b.position)) || []),
        );
        return JSON.parse(JSON.stringify(triggers)); // structuredClone supported by edge and chrome from around Feb 2022
      }),
    );
  }

  private getDirectCallTemplates(): Observable<TriggerTemplate[]> {
    return of(this.settingsService.settings$.value?.templates || []);
  }

  private async fireTriggers(triggers: Trigger[], callInfo: ConversationInfo | null = null): Promise<void> {
    for (const trigger of triggers) {
      // async does not work in forEach
      try {
        this.placeholderService.applyPlaceholders(trigger, callInfo as IncomingCallInfo, this.callTriggersInfo);

        if (trigger.response.wait) {
          await this.fireTrigger(trigger);
        } else {
          this.fireTrigger(trigger);
        }
      } catch {
        this.loggingService.Error(`Trigger failed: ${JSON.stringify(trigger)}`);
      }
    }
  }

  private async fireTrigger(trigger: Trigger): Promise<void> {
    if (trigger.response?.waitTimeout) {
      trigger.response.timeoutSec = moment(trigger.response.waitTimeout, 'HH:mm:ss').diff(moment().startOf('day'), 'seconds');
    }

    // 1. command trigger

    if (trigger.command?.path) {
      await this.executeCommand(trigger);
      return;
    }

    // 2. url trigger

    let url: URL = null;
    try {
      url = new URL(trigger.url);
    } catch {
      this.loggingService.Error(`Trigger url is not valid: ${trigger.url}`);
      return;
    }

    const headers = new Headers();
    trigger.headers.forEach((header) => {
      headers.append(header.key.replace(/\s/g, ''), header.value);
    });

    if (trigger.authorization.type === AuthorizationType.Basic) {
      headers.append('Authorization', `Basic ${btoa(`${trigger.authorization.userName}:${trigger.authorization.password}`)}`);
    }

    if (trigger.authorization.type === AuthorizationType.Scopes || trigger.authorization.type === AuthorizationType.AccessToken) {
      const scopes = trigger.authorization.type === AuthorizationType.Scopes ? trigger.authorization.scopes : null;
      const accessToken = await this.authService.aquireApiToken(scopes || AuthRequest.loginPopup.scopes).toPromise();
      if (accessToken?.accessToken) {
        headers.append('Authorization', `Bearer ${accessToken.accessToken}`);
      }
    }

    if (trigger.httpMethod.toLowerCase() === 'get' || trigger.httpMethod.toLowerCase() === 'post-multipartform') {
      trigger.body = null;
    }

    if (trigger.openInBrowser) {
      this.openTriggerInBrowser(trigger);
      return;
    }

    if (url.protocol !== 'https:') {
      this.loggingService.Error(`Trigger url is not valid: ${trigger.url}`);
      return;
    }

    const controller = new AbortController();
    if (trigger.response?.wait) {
      setTimeout(() => {
        controller.abort();
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      }, trigger.response.timeoutSec * 1000);
    }

    const options = {
      method: trigger.httpMethod,
      body: trigger.body,
      headers,
      signal: controller.signal,
    } as RequestInit;

    try {
      if (trigger.httpMethod.toLowerCase() === 'post-multipartform') {
        const FD = new FormData();
        // Push our data into our FormData object
        trigger.multipartFormData.forEach((param) => {
          FD.append(param.key, param.value);
        });
        options.method = 'post';
        options.body = FD;
      }

      const response = await fetch(trigger.url, options).catch((error) => {
        this.loggingService.Error(`Error calling trigger ${trigger.url} : \n ${JSON.stringify(error)}`);
      });

      await this.processResponse(trigger, response);
    } catch (error) {
      this.loggingService.Error(`Error calling trigger ${trigger.url} : \n ${JSON.stringify(error)}`);
    }
  }

  private async processResponse(trigger: Trigger, response: void | Response): Promise<void> {
    if (!response) {
      return this.loggingService.Error(`TRIGGER call to ${trigger.url} not succeeded.`);
    }

    const responseText = await response.text();
    let responseJson = null;
    try {
      responseJson = JSON.parse(responseText);
    } catch {
      this.loggingService.Error(`Response is not JSON: ${responseText}`);
    }

    if (!response.ok && responseJson?.error) {
      return this.loggingService.Error(responseJson ? responseJson.error : `TRIGGER call to ${trigger.url} not succeeded.`);
    }

    if (!trigger.response.wait) {
      return;
    }

    this.callTriggersInfo[trigger.id] = {
      status: response.status,
      body: responseJson || responseText,
    } as TriggerResponseInfo;
  }

  private openTriggerInBrowser(trigger: Trigger): void {
    const message = new OpenInBrowserMessage();
    message.url = trigger.url;
    message.method = trigger.httpMethod;
    message.body = trigger.body;
    message.userName = trigger.authorization.userName;
    message.password = trigger.authorization.password;

    MessageService.SendOpenInBrowser(message);
  }

  private async executeCommand(trigger: Trigger): Promise<void> {
    const commandId = uuid();

    this.sendCommand(trigger, commandId);

    if (trigger.response?.wait) {
      const response = await this.commandBus.commandCompleted$
        .pipe(
          filter((x) => x.id === commandId),
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          timeout((trigger.response.timeoutSec || 10) * 1000),
          catchError((_) => of(null)),
          filter((x) => !!x),
        )
        .toPromise();

      this.callTriggersInfo[trigger.id] = {
        body: response?.result?.replace(/(\r\n|\n|\r)/gm, ''),
      } as TriggerResponseInfo;
    }

    return;
  }

  private sendCommand(trigger: Trigger, commandId: string): void {
    const message = new CommandMessage();
    message.path = trigger.command.path;
    message.arguments = trigger.command.arguments;
    message.id = commandId;
    message.returnResult = trigger.response?.wait;
    message.timeoutSec = trigger.response.timeoutSec;
    MessageService.SendOpenInBrowser(message);
  }
}
